import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  AddressElement,
  Elements,
  TaxIdElement,
  useElements,
} from '@stripe/react-stripe-js';
import type {
  Appearance,
  StripeAddressElementChangeEvent,
  StripeTaxIdElementChangeEvent,
} from '@stripe/stripe-js';

import { getStripePromise } from 'utils/stripeClient';

import * as Ch from './styled';

export type InvoiceBillingData = {
  address: StripeAddressElementChangeEvent['value'];
  taxId: StripeTaxIdElementChangeEvent['value'] | null;
};

export type InvoiceChfBillingHandle = {
  getBillingData: () => InvoiceBillingData | null;
};

type InvoiceChfBillingFieldsProps = {
  active: boolean;
  defaultCountry?: string;
  onReadyChange?: (ready: boolean) => void;
};

const stripeAppearance: Appearance = {
  theme: 'stripe',
  variables: {
    borderRadius: '10px',
    fontSizeBase: '14px',
  },
};

type InnerProps = {
  defaultCountry: string;
  onReadyChange?: (ready: boolean) => void;
};

const InvoiceChfBillingFieldsInner = forwardRef<
  InvoiceChfBillingHandle,
  InnerProps
>(function InvoiceChfBillingFieldsInner(
  { defaultCountry, onReadyChange },
  ref,
) {
  const { t } = useTranslation();
  const elements = useElements();
  const [addressData, setAddressData] = useState<
    StripeAddressElementChangeEvent['value'] | null
  >(null);
  const [taxIdData, setTaxIdData] = useState<
    StripeTaxIdElementChangeEvent['value'] | null
  >(null);
  const [addressComplete, setAddressComplete] = useState(false);
  const [taxIdComplete, setTaxIdComplete] = useState(false);

  useEffect(() => {
    onReadyChange?.(addressComplete && taxIdComplete);
  }, [addressComplete, taxIdComplete, onReadyChange]);

  useImperativeHandle(
    ref,
    () => ({
      getBillingData: () => {
        if (!addressData) return null;
        return { address: addressData, taxId: taxIdData };
      },
    }),
    [addressData, taxIdData],
  );

  const addressOptions = useMemo(
    () => ({
      mode: 'billing' as const,
      defaultValues: {
        address: { country: defaultCountry },
      },
    }),
    [defaultCountry],
  );

  return (
    <>
      <Ch.InvoiceFieldLabel>
        {t('subscriptions.checkout.invoiceBillingAddressLabel')}
      </Ch.InvoiceFieldLabel>
      <Ch.StripeElementField>
        <AddressElement
          options={addressOptions}
          onChange={(event) => {
            setAddressComplete(event.complete);
            if (event.complete) setAddressData(event.value);
            else setAddressData(null);
          }}
        />
      </Ch.StripeElementField>
      <Ch.InvoiceFieldLabel>
        {t('subscriptions.checkout.invoiceBillingTaxIdLabel')}
      </Ch.InvoiceFieldLabel>
      <Ch.StripeElementField>
        <TaxIdElement
          options={{ visibility: 'always' }}
          onChange={(event) => {
            setTaxIdComplete(event.complete);
            if (event.complete) setTaxIdData(event.value);
            else setTaxIdData(null);
          }}
        />
      </Ch.StripeElementField>
    </>
  );
});

const stripePromise = getStripePromise();

const InvoiceChfBillingFields = forwardRef<
  InvoiceChfBillingHandle,
  InvoiceChfBillingFieldsProps
>(function InvoiceChfBillingFields(
  { active, defaultCountry = 'CH', onReadyChange },
  ref,
) {
  const { t } = useTranslation();

  if (!active) return null;

  if (!stripePromise) {
    return (
      <Ch.InvoiceFormError role="alert">
        {t('subscriptions.checkout.invoiceBillingStripeMissing')}
      </Ch.InvoiceFormError>
    );
  }

  return (
    <Ch.InvoiceBillingSection>
      <Ch.InvoiceFieldLabel as="p">
        {t('subscriptions.checkout.invoiceBillingSectionTitle')}
      </Ch.InvoiceFieldLabel>
      <Ch.InvoiceHint>
        {t('subscriptions.checkout.invoiceBillingSectionHint')}
      </Ch.InvoiceHint>
      <Elements
        stripe={stripePromise}
        options={{ appearance: stripeAppearance }}
      >
        <InvoiceChfBillingFieldsInner
          ref={ref}
          defaultCountry={defaultCountry}
          onReadyChange={onReadyChange}
        />
      </Elements>
    </Ch.InvoiceBillingSection>
  );
});

export default InvoiceChfBillingFields;
